/**
 * SystemPanel
 * -----------
 *
 * September 2026
 */

import * as React from "react";

import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";

import parser from "papaparse";

const systemWarnings = {
  trials: function (newCount) {
    return newCount <= 0 ? "Trial count must be positive" : null;
  },
  duration: function (newDuration) {
    return newDuration <= 0 ? "Sim duration must be positive" : null;
  },
  time_interval: function (newTimeInterval) {
    return newTimeInterval <= 0.0 ? "Interval must be positive" : null;
  },
  default: () => null,
};

/**
 * Configuration sub-panel for editing the simulation model's system attributes.
 *
 * @param {Object} config               - Configuration object for the simulation model.
 * @param {Function} setConfig          - Setter for config object.
 * @param {Object} configWarnings       - Verification warnings for entity types.
 * @param {Function} setConfigWarnings  - Setter for verification warnings.
 */
function SystemPanel({ config, setConfig, configWarnings, setConfigWarnings }) {
  // State hook managing current section to display.
  const [sectionIndex, setSectionIndex] = React.useState(0);

  // Ref hook for the FileUpload component.
  const fileUpload = React.useRef(null);

  // Ref hook for the Toast component.
  const parseCsvToast = React.useRef(null);

  /**
   * Updates warnings for system parameters.
   *
   * @param {String} parameter    - Parameter key of the system attribute.
   * @param {String} warning      - Warning message.
   */
  const updateSystemWarnings = (parameter, warning) => {
    setConfigWarnings((prev) => {
      const newWarnings = { ...prev, system: { ...prev.system } };
      if (warning) {
        newWarnings.system[parameter] = warning;
      } else {
        delete newWarnings.system[parameter];
      }

      return newWarnings;
    });
  };

  /**
   * Updates a system attribute.
   *
   * @param {Number} newValue     - Updated value of the system attribute.
   * @param {String} inputType    - Attribute key of system parameter.
   */
  const handleSystemInput = (newValue, inputType) => {
    updateSystemWarnings(inputType, (systemWarnings[inputType] ?? systemWarnings.default)(newValue));

    const newConfig = {...config, system: { ...config.system, [inputType]: newValue }};
    setConfig(newConfig);
  };

  /**
   * Updates the simulation's time unit.
   *
   * @param {String} setUnit - Updated time unit.
   */
  const handleTimeUnit = (setUnit) => {
    console.log("New simulation time unit is " + setUnit);

    const newConfig = {...config, system: { ...config.system, time_unit: setUnit }};
    setConfig(newConfig);
  };

  /**
   * Enables/Disables the scheduling of resource arrivals.
   *
   * @param {*} isEnabled - Flag to enable the parsing of a resource arrival file.
   */
  const switchResourceArrivals = (isEnabled) => {
    const newArrivals = {...config.system.resource_arrivals, enabled: isEnabled};
    const newSystemParams = {...config.system, resource_arrivals: newArrivals};
    const newConfig = { ...config, system: newSystemParams };

    setConfig(newConfig);
  };

  /**
   * Parses resource arrival data from a csv file into a script.
   *
   * @param {Object} event    - File being uploaded.
   */
  const parseResourceArrivals = async (event) => {
    const file = event.files[0];

    // Verify file size wasn't breached.
    if (!file) {
      parseCsvToast.current.show({
        severity: "error",
        summary: "Error",
        detail: "File surpassed size limit.",
        life: 3000,
      });
      return;
    }

    parser.parse(file, {
      header: true,
      complete: (results) => {
        try {
          let resourceArrivalScript = [];
          results.data.forEach((row, index) => {
            const timeField = row["time"].trim();
            const gid = row["gid"].trim();
            const amountField = row["quantity"].trim();

            // Verify format of csv entries.
            const rowNum = index + 2;
            if (!timeField) {
              throw new Error(`Row ${rowNum}: timemark is missing.`);
            }

            const timemark = Number(timeField);
            if (!Number.isFinite(timemark) || timemark < 0) {
              throw new Error(
                `Row ${rowNum}: timemark isn't a positive float.`,
              );
            }

            if (!gid) {
              throw new Error(`Row ${rowNum}: gid is missing.`);
            }

            if (!(gid in config.resources)) {
              throw new Error(`Row ${rowNum}: resource gid doesn't exist.`);
            }

            if (!amountField) {
              throw new Error(`Row ${rowNum}: amount is missing.`);
            }

            const amount = Number(amountField);
            if (!Number.isInteger(amount) || amount <= 0) {
              throw new Error(
                `Row ${rowNum}: amount isn't a positive integer.`,
              );
            }

            // Add row data as entry in arrival script.
            resourceArrivalScript.push({
              time_mark: timemark,
              gid: gid,
              amount: amount,
            });
          });

          // Verify script isn't empty.
          if (resourceArrivalScript.length === 0) {
            throw new Error("File contained no resource arrivals.");
          } else {
            const newArrivals = {...config.system.resource_arrivals, script: resourceArrivalScript};
            const newSystemParams = {...config.system, resource_arrivals: newArrivals};
            const newConfig = {...config, system: newSystemParams};
            setConfig(newConfig);
            console.log(newConfig);
          }
        } catch (error) {
          console.error("Failed to parse csv file: ", error.message);
          parseCsvToast.current.show({severity: "error", summary: "Error", detail: error.message, life: 3000});
        }
      },
    });
    fileUpload.current.clear();
  };

  /**
   * New index for a panel section.
   *
   * @param {Number} newSection
   */
  const handleSectionChange = (newSection) => {
    setSectionIndex(newSection);
  };

  /**
   * Panel section for the simulation model's system attributes.
   *
   * @returns Child component for the "System Parameters" section.
   */
  const systemParamSection = () => {
    return (
      <div>
        <div>
          <label style={{ marginRight: "1rem" }}>
            Number of Simulation Trials:
          </label>
          <InputNumber
            value={config.system.trials}
            onValueChange={(event) =>
              handleSystemInput(event.target.value, "trials")
            }
            placeholder="Enter value"
            min={0}
            useGrouping={false}
            invalid={!!configWarnings.system?.trials}
            inputStyle={{ width: "110px" }}
          />
        </div>
        <Divider />
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ marginRight: "1rem" }}>Simulation Duration:</label>
          <InputNumber
            value={config.system.duration}
            onValueChange={(event) =>
              handleSystemInput(event.target.value, "duration")
            }
            placeholder="Enter value"
            min={0}
            useGrouping={false}
            invalid={!!configWarnings.system?.duration}
            inputStyle={{ width: "110px" }}
          />
        </div>
        <div>
          <label style={{ marginRight: "1rem" }}>Time Unit:</label>
          <Dropdown
            value={config.system.time_unit}
            options={["years", "weeks", "days", "hours", "minutes", "seconds"]}
            onChange={(event) => handleTimeUnit(event.target.value)}
          />
        </div>
        <Divider />
        <div>
          <label style={{ marginRight: "1rem" }}>Chart Time Interval:</label>
          <InputNumber
            value={config.system.time_interval}
            onValueChange={(event) =>
              handleSystemInput(event.target.value, "time_interval")
            }
            placeholder="Enter value"
            allowEmpty={false}
            min={0}
            minFractionDigits={2}
            useGrouping={false}
            invalid={!!configWarnings.system?.time_interval}
            inputStyle={{ width: "110px" }}
          />
        </div>
      </div>
    );
  };

  /**
   * Panel section for configuring salabim's resource allocation methods.
   * 
   * @returns Child component for the "Resource Allocation Methods" section.
   */
  const resourceAllocationMethodSection = () => {
    return (
        <div>
            <div>
                <Checkbox
                    checked={config.system.honor_only_first}
                    onChange={(event) => handleSystemInput(event.checked, "honor_only_first")}
                    style={{ marginRight: "1rem" }}
                />
                <label>
                    Honor only first request
                </label>
            </div>
            <Divider/>
            <div>
                <Checkbox
                    checked={config.system.honor_only_highest_priority}
                    onChange={(event) => handleSystemInput(event.checked, "honor_only_highest_priority")}
                    style={{ marginRight: "1rem" }}
                />
                <label>
                    Honor only highest priority
                </label>
            </div>
        </div>
    )
  }

  /**
   * Panel section for scripting the arrivals of resources.
   *
   * @returns Child component for the "Simulation Scripting" section.
   */
  const simScriptSection = () => {
    return (
      <div>
        <Toast ref={parseCsvToast} position="center" />
        <div style={{ marginBottom: "1rem" }}>
          <Checkbox
            checked={config.system.resource_arrivals.enabled}
            onChange={(event) => switchResourceArrivals(event.checked)}
            style={{ marginRight: "1rem" }}
          />
          <label>Enable Resource Arrival Script</label>
        </div>
        <FileUpload
          ref={fileUpload}
          mode="basic"
          auto
          customUpload
          onSelect={(event) => parseResourceArrivals(event)}
          chooseLabel="Upload File"
          accept=".csv,text/csv"
          maxFileSize={1000000}
          disabled={!config.system.resource_arrivals.enabled}
        />
      </div>
    );
  };

  const sections = [
    systemParamSection(),
    resourceAllocationMethodSection(),
    simScriptSection()
  ];
  const dropdownSections = [
    { label: "System Parameters", value: 0 },
    { label: "Resource Allocation Methods", value: 1 },
    { label: "Simulation Scripting", value: 2 }
  ];

  return (
    <Card style={{ width: "450px", height: "500px" }}>
      <Dropdown
        value={sectionIndex}
        onChange={(event) => setSectionIndex(event.value)}
        options={dropdownSections}
        optionLabel="label"
        optionValue="value"
        style={{ marginBottom: "2rem" }}
        valueTemplate={(option) => (
          <span style={{ fontSize: "1.17em", fontWeight: "bold" }}>
            {option?.label}
          </span>
        )}
      />
      <div style={{ height: "300px", overflow: "auto" }}>
        {sections[sectionIndex]}
      </div>
      <Paginator
        first={sectionIndex}
        rows={1}
        totalRecords={3}
        onPageChange={(event) => handleSectionChange(event.first)}
        template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
      />
    </Card>
  );
}

export default SystemPanel;
